import { useEffect, useState, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { AuthProvider, useAuth } from './context/AuthContext'
import { WeddingProvider } from './context/WeddingContext'
import LandingPage from './pages/LandingPage'
import PricingPage from './pages/PricingPage'
import LoginPage from './pages/auth/LoginPage'
import SignupPage from './pages/auth/SignupPage'
import AuthCallback from './pages/auth/AuthCallback'
import OnboardingPage from './pages/auth/OnboardingPage'
import Dashboard from './pages/dashboard/Dashboard'
import AdminPage from './pages/AdminPage'
import SupportDashboard from './pages/SupportDashboard'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import FeaturesPage from './pages/FeaturesPage'
import AboutPage from './pages/AboutPage'
import DocsPage from './pages/DocsPage'
import JoinWeddingPage from './pages/JoinWeddingPage'
import NotFoundPage from './pages/NotFoundPage'
import WeddingMiniSite from './pages/guest/WeddingMiniSite'
import CheckinPage from './pages/guest/CheckinPage'
import AmbassadorPage from './pages/guest/AmbassadorPage'
import AccountPickerPage from './pages/auth/AccountPickerPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import RSVPPage from './pages/guest/RSVPPage'
import WrappedPage from './pages/guest/WrappedPage'
import StoriesPage from './pages/StoriesPage'
import WaitlistPage from './pages/WaitlistPage'
import BecomeVendorPage from './pages/aisle/BecomeVendorPage'
import VendorDashboard from './pages/vendor/VendorDashboard'
import { FlagGate } from './lib/featureFlags'
import { fetchLaunch } from './lib/launch'
import InstallPrompt from './components/InstallPrompt'
import ErrorBoundary from './components/ErrorBoundary'

function LoadingScreen() {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center',
      height:'100vh', background:'#FAFAF8',
      animation:'okoiyaFadeOut 0.4s ease 2.1s forwards',
    }}>
      <style>{`
        @keyframes okoiyaFadeOut {
          to { opacity: 0; pointer-events: none; }
        }
        @keyframes okoiyaLogoIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes okoiyaLogoPulse {
          0%, 100% { transform: scale(1);    opacity: 1;   }
          50%       { transform: scale(1.07); opacity: 0.6; }
        }
        .okoiya-load-wrap {
          animation: okoiyaLogoIn 0.4s ease-out both;
        }
        .okoiya-load-logo {
          animation: okoiyaLogoPulse 1.4s ease-in-out 0.35s infinite;
        }
      `}</style>
      <div className="okoiya-load-wrap">
        <img
          src="/okoiya-logo.png"
          alt="Okoiya"
          className="okoiya-load-logo"
          style={{ height:52, objectFit:'contain', display:'block' }}
        />
      </div>
    </div>
  )
}

// Routes the public can still reach while the site is sealed pre-launch.
// Auth routes stay open so an admin can log in and unlock the rest.
const PRELAUNCH_ALLOW = [
  '/waitlist', '/founding', '/features', '/privacy', '/terms',
  '/login', '/auth/callback', '/reset-password', '/account-picker',
  '/admin',   // safety valve: an admin can always reach the switch to flip it back
]

// Master launch gate. When the site isn't live, everyone except admins is
// funnelled to the waitlist — but the allow-list pages still render.
function LaunchGate({ children }) {
  const { user, loading: authLoading } = useAuth()
  const location = useLocation()
  const [state, setState] = useState(null)   // { live, isAdmin }

  useEffect(() => {
    if (authLoading) return
    let cancelled = false
    ;(async () => {
      const { live } = await fetchLaunch()
      let isAdmin = false
      if (!live && user) {
        try { const { data } = await supabase.rpc('is_okoiya_admin'); isAdmin = !!data } catch { /* treat as non-admin */ }
      }
      if (!cancelled) setState({ live, isAdmin })
    })()
    return () => { cancelled = true }
  }, [authLoading, user])

  if (authLoading || !state) return <LoadingScreen/>
  if (!state.live && !state.isAdmin) {
    const p = location.pathname
    const allowed = PRELAUNCH_ALLOW.some(a => p === a || p.startsWith(a + '/'))
    if (!allowed) return <Navigate to="/waitlist" replace/>
  }
  return children
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen/>
  if (!user) return <Navigate to="/login" replace/>
  return children
}
function PublicRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen/>
  if (user) return <Navigate to="/dashboard" replace/>
  return children
}

function MaintenanceBanner() {
  const [m, setM] = useState(null)
  useEffect(() => {
    supabase.from('admin_settings').select('value').eq('key', 'maintenance').maybeSingle()
      .then(({ data }) => { if (data?.value?.enabled) setM(data.value) }, () => {})
  }, [])
  if (!m) return null
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:9998, background:'#1A1A1A', color:'#fff', textAlign:'center', padding:'0.6rem 1rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:600 }}>
      🛠 {m.message || 'Okoiya is undergoing scheduled maintenance. Some features may be unavailable.'}
    </div>
  )
}

// Keeps the phone's back button inside Okoiya: normal back still walks between
// in-app pages, but a back press that WOULD leave the app (at a top-level screen)
// is caught once and shows "press back again to exit" instead of closing it.
// Only engages on app screens — public/marketing pages keep normal back behaviour.
const APP_ROUTE = /^\/(dashboard|admin|support|vendor|w|ambassador|checkin|wrapped)(\/|$)/
function BackExitGuard() {
  const location = useLocation()
  const navType  = useNavigationType()
  const depth    = useRef(0)        // how many in-app pages deep we are
  const armed    = useRef(false)    // a second back within the window leaves
  const [toast, setToast] = useState(false)

  // Track the in-app history depth from router push/pop events.
  useEffect(() => {
    if (navType === 'PUSH') depth.current += 1
    else if (navType === 'POP') depth.current = Math.max(0, depth.current - 1)
  }, [location.key, navType])

  useEffect(() => {
    const isApp = (p) => APP_ROUTE.test(p)
    if (isApp(window.location.pathname)) window.history.pushState({ okoiyaGuard: true }, '')
    const onPop = () => {
      if (!isApp(window.location.pathname)) return  // public page → allow leaving
      if (depth.current > 0) return                 // still pages to go back to → normal back
      if (armed.current) return                     // confirmed → let it leave
      window.history.pushState({ okoiyaGuard: true }, '')  // stay in the app
      armed.current = true
      setToast(true)
      setTimeout(() => { armed.current = false; setToast(false) }, 2200)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  if (!toast) return null
  return (
    <div style={{ position:'fixed', left:'50%', bottom:'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)', transform:'translateX(-50%)', zIndex:99999, background:'rgba(26,26,26,0.92)', color:'#fff', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:600, padding:'0.7rem 1.25rem', borderRadius:100, boxShadow:'0 8px 24px rgba(0,0,0,0.25)', pointerEvents:'none', whiteSpace:'nowrap' }}>
      Press back again to exit Okoiya
    </div>
  )
}

export default function App() {
  // Count one website visit per browser session
  useEffect(() => {
    // Count one visit per browser per calendar day (UTC, to match the server's
    // current_date). Reopening the browser the same day won't recount.
    const today = new Date().toISOString().slice(0, 10)
    if (localStorage.getItem('okoiya_visit_day') === today) return
    localStorage.setItem('okoiya_visit_day', today)
    supabase.rpc('bump_daily_visit').then(() => {}, () => {})
  }, [])

  return (
    <ErrorBoundary>
    <MaintenanceBanner/>
    <BrowserRouter>
      <BackExitGuard/>
      <AuthProvider>
        <LaunchGate>
        <Routes>
          <Route path="/" element={<LandingPage/>}/>
          <Route path="/pricing" element={<WeddingProvider><PricingPage/></WeddingProvider>}/>
          <Route path="/auth/callback" element={<AuthCallback/>}/>
          <Route path="/account-picker" element={<AccountPickerPage/>}/>
          <Route path="/reset-password" element={<ResetPasswordPage/>}/>
          <Route path="/login" element={<PublicRoute><LoginPage/></PublicRoute>}/>
          <Route path="/signup" element={<PublicRoute><SignupPage/></PublicRoute>}/>
          <Route path="/onboarding" element={<PrivateRoute><OnboardingPage/></PrivateRoute>}/>
          <Route path="/dashboard/*" element={<PrivateRoute><WeddingProvider><Dashboard/></WeddingProvider></PrivateRoute>}/>
          <Route path="/admin/*" element={<PrivateRoute><AdminPage/></PrivateRoute>}/>
          <Route path="/support" element={<PrivateRoute><SupportDashboard/></PrivateRoute>}/>
          <Route path="/vendor" element={<PrivateRoute><FlagGate flag="become_vendor"><VendorDashboard/></FlagGate></PrivateRoute>}/>
          <Route path="/w/:weddingSlug" element={<WeddingMiniSite/>}/>
          <Route path="/checkin/:token" element={<CheckinPage/>}/>
          <Route path="/ambassador/:token" element={<AmbassadorPage/>}/>
          <Route path="/w/:weddingSlug/*" element={<WeddingMiniSite/>}/>
          <Route path="/w/:weddingSlug/invite/:token" element={<RSVPPage/>}/>
          <Route path="/wrapped/:weddingSlug" element={<WrappedPage/>}/>
          <Route path="/features" element={<FeaturesPage/>}/>
          <Route path="/about"    element={<FlagGate flag="about" redirect="/"><AboutPage/></FlagGate>}/>
          <Route path="/stories"  element={<StoriesPage/>}/>
          <Route path="/waitlist" element={<WaitlistPage/>}/>
          <Route path="/founding" element={<Navigate to="/waitlist" replace/>}/>
          <Route path="/aisle/become-a-vendor" element={<FlagGate flag="become_vendor" redirect="/"><BecomeVendorPage/></FlagGate>}/>
          <Route path="/docs"     element={<DocsPage/>}/>
          <Route path="/terms"    element={<TermsPage/>}/>
          <Route path="/privacy"  element={<PrivacyPage/>}/>
          <Route path="/join/:token" element={<JoinWeddingPage/>}/>
          <Route path="*" element={<NotFoundPage/>}/>
        </Routes>
        </LaunchGate>
      </AuthProvider>
        <InstallPrompt/>
    </BrowserRouter>
    </ErrorBoundary>
  )
}
